import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';

import type { AuthStatus } from '#modules/user/features/auth/utils/checkIsAuth';
import { createQueryClient } from '#features/api/utils/queryClient';
import { DefaultCatchBoundary } from '#features/error/components/DefaultCatchBoundary';
import { getActiveI18n } from '#features/i18n/utils/getActiveI18n';
import { setupLinguiSsrIntegration } from '#features/i18n/utils/setupLinguiSsrIntegration';
import { syncOnlineAtBoot } from '#features/network/model/connectivity';
import { envConfig } from '#constants/envConfig';
import { routeTree } from './routeTree.gen';

/**
 * TanStack Start resolves `src/router.tsx` as its router entry and calls `getRouter()` itself.
 *
 * <p>On the server it calls it **per request**, and the router carries request-scoped state — the
 * active i18n locale above all. Memoising it there pins whatever locale the first request happened
 * to resolve onto every later one, so /ru bounces back to / on an English-warmed process.
 *
 * <p>On the client there is exactly one router for the page's lifetime, and our client entry needs
 * that very instance for Sentry tracing — a second one would be a router nobody navigates. So the
 * promise is memoised on the client only.
 */
let clientRouterPromise: ReturnType<typeof createRouter> | undefined;

export function getRouter() {
  if (envConfig.IN_SERVER) {
    return createRouter();
  }
  clientRouterPromise ??= createRouter();
  return clientRouterPromise;
}

/**
 * Initial auth context for the app.
 *
 * The web build starts as a guest and lets the server decide per request. The app has no such
 * server: it boots from a shell prerendered at build time, where nobody was signed in, so
 * starting as a guest means the first render is wrong for everyone who is logged in — the auth
 * guard bounces them to /sign-in, and fixing it afterwards makes the whole navigation flicker.
 * Reading the stored token here costs one await before the first render and gets it right the
 * first time.
 */
async function initialAuthContext(): Promise<{ isAuth: boolean; authStatus: AuthStatus }> {
  if (!envConfig.IS_NATIVE || envConfig.IN_SERVER) {
    return { isAuth: false, authStatus: 'pending' };
  }
  try {
    const [{ getNativeToken }, { isLikelyJwtToken }] = await Promise.all([
      import('#features/api/utils/nativeAuth'),
      import('#modules/user/features/auth/utils/isLikelyJwtToken'),
    ]);
    return { isAuth: isLikelyJwtToken(await getNativeToken()), authStatus: 'ok' };
  } catch {
    // Couldn't read storage — say "don't know" rather than "signed out", so the guard
    // doesn't throw a valid session out on a transient failure.
    return { isAuth: false, authStatus: 'pending' };
  }
}

/**
 * Приложение: последние данные с телефона - в кеш до первого кадра (features/snapshot), чтобы
 * лента, профиль и чаты открывались сразу и без сети. Сравнение с флагом стоит прямо здесь, а не
 * через envConfig: так сборщик сворачивает его в false и не тянет модуль в веб.
 */
async function restoreSnapshot(queryClient: ReturnType<typeof createQueryClient>): Promise<void> {
  if (import.meta.env.VITE_IS_NATIVE !== 'true' || envConfig.IN_SERVER) {
    return;
  }
  try {
    const { bootSnapshots } = await import('#features/snapshot/boot');
    await bootSnapshots(queryClient);
  } catch {
    // Без снимка приложение просто стартует как раньше - из сети.
  }
}

async function createRouter() {
  const i18n = await getActiveI18n();
  const queryClient = createQueryClient();

  // Приложение: без сети запросы встают на паузу с первого же, а не падают по очереди (см.
  // `syncOnlineAtBoot`). Со снимком или без - всё равно.
  if (envConfig.IS_NATIVE && !envConfig.IN_SERVER) {
    syncOnlineAtBoot();
  }

  const [{ isAuth, authStatus }] = await Promise.all([
    initialAuthContext(),
    restoreSnapshot(queryClient),
  ]);

  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    //defaultPreload: 'intent',
    // Route changes must start at the target position immediately. `auto` inherits
    // the global smooth scrolling and opens the next page partway down the old scroll.
    scrollRestorationBehavior: 'instant',
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    // routeMasks: [dashboardPostToPostMask],
    context: { queryClient, i18n, isAuth, authStatus },
    // defaultPreload: 'intent',
    defaultErrorComponent: DefaultCatchBoundary,
    /*
      Экран ожидания не держится дольше загрузки. По умолчанию роутер показывает его минимум
      500 мс, и в приложении это стоило пустых полсекунды на каждом холодном старте: оболочка
      приложения собрана заранее (SPA), первый маршрут под корнем при гидрации считается «в пути»,
      и роутер держал его ожидание эти 500 мс уже после того, как всё загрузилось. Своего экрана
      ожидания у маршрутов почти нет, так что держался пустой экран.

      Маршрут с настоящим скелетом, которому мигание вредит, ставит себе `pendingMinMs` сам (см.
      профиль игрока).
    */
    defaultPendingMinMs: 0,
    defaultStructuralSharing: true,
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });

  setupLinguiSsrIntegration({
    router,
    i18n,
    wrapQueryClient: true,
  });

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>;
  }

  // Routes whose component renders its own footer (e.g. the landing) set this so
  // MainLayout suppresses the global footer instead of double-rendering it.
  // `rendersOwnHeader` does the same for the global site header: product surfaces
  // that ship their own chrome (the embedded LFG bot landing, the Discord admin)
  // set it so MainLayout doesn't stack the teamplay.gg header on top of theirs.
  interface StaticDataRouteOption {
    rendersOwnFooter?: boolean;
    rendersOwnHeader?: boolean;
  }

  /**
   * Что переход рассказывает о себе следующему экрану.
   *
   * `fromChatList` ставит строка списка переписок. По ней системная «Назад» в самой переписке
   * понимает, что список уже лежит шагом раньше, и отматывает историю вместо нового перехода -
   * иначе следующее нажатие возвращало бы человека в тот же список, то есть впустую.
   */
  interface HistoryState {
    fromChatList?: boolean;
  }
}
